import { format, isValid, parseISO, subDays } from 'date-fns';
import type { SalesforceAssetDetail } from './sales-force-asset-reconciliation';
import type { ContractSource } from './sales-force-contract-reconciliation';

export type SalesforceInternalSeverity = 'error' | 'warning' | 'unevaluable';
export type SalesforceInternalRule =
  | 'IncompleteAssetEvidence' | 'UnknownAssetStatus' | 'UnknownRecurrence'
  | 'MissingContract' | 'ContractNotFetched' | 'AmbiguousContract' | 'MissingAccount'
  | 'AccountMismatch' | 'UnknownContractStatus' | 'InvalidDate'
  | 'ActiveOnCancelledContract' | 'ActiveOnExpiredContract' | 'ActiveOnDraftContract'
  | 'ActiveOnPastEndedContract' | 'UsageBeyondContractEnd'
  | 'MissingUsageEnd' | 'PastUsageEnd' | 'MissingRenewalDate' | 'RenewalMisalignment'
  | 'ActiveContractWithoutAssets' | 'IncompleteContractAssets' | 'ActiveAfterCancellation'
  | 'InconsistentAssetEndDates' | 'ActivatedWithPastEndDate' | 'ExpiredWithoutEndDate'
  | 'ExpiredWithCurrentEndDate' | 'SalesforceRenewalDatePassed' | 'InvalidQuantity'
  | 'ActiveZeroQuantity' | 'ContractEndBeforeStart' | 'ContractRenewalBeforeStart'
  | 'MissingContractIdentity' | 'IncompleteLifecycleEvidence' | 'AssetStartAfterEnd'
  | 'ActiveOnFutureContract' | 'CancellationBeforeAssetStart' | 'CancelledWithoutCancellationDate';

export interface SalesforceInternalFinding {
  rule: SalesforceInternalRule;
  severity: SalesforceInternalSeverity;
  asset: SalesforceAssetDetail | null;
  relatedAssets: SalesforceAssetDetail[];
  contract: ContractSource | null;
  actual: string | null;
  expected: string | null;
}

export function salesforceCalendarDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day.startsWith('0001-')) return null;
  const parsed = parseISO(day);
  return isValid(parsed) && format(parsed, 'yyyy-MM-dd') === day ? day : null;
}

export function validateSalesforceInternal(
  assets: SalesforceAssetDetail[], contracts: ContractSource[], comparedAt: string,
): SalesforceInternalFinding[] {
  const today = salesforceCalendarDate(comparedAt);
  if (!today) throw new Error('Salesforce validation requires a valid evaluation date.');
  const byId = new Map<string, ContractSource[]>();
  for (const contract of contracts) {
    if (contract.id) byId.set(contract.id, [...(byId.get(contract.id) ?? []), contract]);
  }
  const findings: SalesforceInternalFinding[] = [];
  const status = (value: string | null | undefined) => value?.trim().toLowerCase();
  const isRecurring = (asset: SalesforceAssetDetail) => !asset.isSupport && status(asset.recurrence) === 'recurring';
  const isNonRecurring = (asset: SalesforceAssetDetail) => asset.isSupport || status(asset.recurrence) === 'one-off';
  const addFinding = (rule: SalesforceInternalRule, severity: SalesforceInternalSeverity,
    asset: SalesforceAssetDetail | null, contract: ContractSource | null,
    actual: string | null = null, expected: string | null = null,
    relatedAssets: SalesforceAssetDetail[] = asset ? [asset] : []) =>
    findings.push({ rule, severity, asset, contract, actual, expected, relatedAssets });
  const assetsByContract = new Map<string, SalesforceAssetDetail[]>();
  const unlinkedAssets: SalesforceAssetDetail[] = [];
  for (const asset of assets) {
    if (asset.contractId) {
      const linked = assetsByContract.get(asset.contractId);
      if (linked) linked.push(asset);
      else assetsByContract.set(asset.contractId, [asset]);
    }
    else unlinkedAssets.push(asset);
  }
  for (const asset of assets) {
    const matches = asset.contractId ? byId.get(asset.contractId) ?? [] : [];
    let contract: ContractSource | null = matches.length === 1 ? matches[0] : null;
    const add = (rule: SalesforceInternalRule, severity: SalesforceInternalSeverity,
      actual: string | null = null, expected: string | null = null) =>
      addFinding(rule, severity, asset, contract, actual, expected);
    if (!asset.validationFieldsComplete) {
      add('IncompleteAssetEvidence', 'unevaluable');
      continue;
    }
    const assetStatus = status(asset.status);
    const cancellation = salesforceCalendarDate(asset.cancellationDate);
    if (isRecurring(asset)) {
      if (!asset.lifecycleFieldsComplete) add('IncompleteLifecycleEvidence', 'unevaluable');
      else {
        const installed = salesforceCalendarDate(asset.installDate);
        const usageEnd = salesforceCalendarDate(asset.usageEndDate);
        if ((asset.installDate != null && !installed)
          || (assetStatus !== 'active' && ((asset.usageEndDate != null && !usageEnd)
            || (asset.cancellationDate != null && !cancellation))))
          add('InvalidDate', 'unevaluable');
        if (installed && usageEnd && installed > usageEnd)
          add('AssetStartAfterEnd', 'error', installed, usageEnd);
        if (installed && cancellation && cancellation < installed)
          add('CancellationBeforeAssetStart', 'warning', cancellation, installed);
        if (assetStatus === 'cancelled' && asset.cancellationDate == null)
          add('CancelledWithoutCancellationDate', 'warning');
      }
    }
    if (assetStatus === 'inactive' || assetStatus === 'cancelled') continue;
    if (assetStatus !== 'active') {
      add('UnknownAssetStatus', 'unevaluable', asset.status);
      continue;
    }
    const recurrence = asset.recurrence?.trim().toLowerCase();
    if (isRecurring(asset)) {
      if (asset.quantity == null || !Number.isInteger(asset.quantity)
        || asset.quantity < 0 || asset.quantity > 2147483647)
        add('InvalidQuantity', 'warning', asset.quantity?.toString() ?? null);
      else if (asset.quantity === 0) add('ActiveZeroQuantity', 'warning', '0');
      if (asset.cancellationDate != null && !cancellation) add('InvalidDate', 'unevaluable', asset.cancellationDate);
      else if (cancellation && cancellation <= today) add('ActiveAfterCancellation', 'error', cancellation, today);
    }
    if (!asset.contractId) {
      if (!asset.isSupport && recurrence !== 'one-off') {
        if (recurrence === 'recurring') add('MissingContract', 'error');
        else add('UnknownRecurrence', 'unevaluable', asset.recurrence ?? null);
      }
      continue;
    }
    if (matches.length !== 1) {
      add(matches.length === 0 ? 'ContractNotFetched' : 'AmbiguousContract', 'unevaluable', asset.contractId);
      continue;
    }
    contract = matches[0];
    if (!asset.accountId || !contract.accountId) add('MissingAccount', 'unevaluable');
    else if (asset.accountId !== contract.accountId)
      add('AccountMismatch', 'error', asset.accountId, contract.accountId);
    if (asset.isSupport || recurrence === 'one-off') continue;
    if (recurrence !== 'recurring') {
      add('UnknownRecurrence', 'unevaluable', asset.recurrence ?? null);
      continue;
    }

    const start = salesforceCalendarDate(contract.startDate);
    if (start && start > today) add('ActiveOnFutureContract', 'error', start, today);
    const end = salesforceCalendarDate(contract.terminationDate);
    const usageEnd = salesforceCalendarDate(asset.usageEndDate);
    const renewal = salesforceCalendarDate(contract.nextRenewalDate);
    const datesInvalid = (contract.terminationDate != null && !end)
      || (asset.usageEndDate != null && !usageEnd) || (contract.nextRenewalDate != null && !renewal);
    if (datesInvalid) {
      add('InvalidDate', 'unevaluable');
      continue;
    }
    const contractStatus = contract.status?.trim().toLowerCase();
    if (contractStatus === 'cancelled') add('ActiveOnCancelledContract', 'error',
      contract.status, end);
    else if (contractStatus === 'expired') add('ActiveOnExpiredContract', 'warning', contract.status, end);
    else if (contractStatus === 'draft') add('ActiveOnDraftContract', 'warning', contract.status);
    else if (contractStatus !== 'activated') add('UnknownContractStatus', 'unevaluable', contract.status);
    if (end && end < today) add('ActiveOnPastEndedContract', 'error', end, today);
    if (end && usageEnd && usageEnd > end) add('UsageBeyondContractEnd', 'warning', usageEnd, end);
    if (!usageEnd) add('MissingUsageEnd', 'warning');
    else if (usageEnd < today) add('PastUsageEnd', 'warning', usageEnd, today);

    if (contractStatus !== 'activated' || (end && end < today)
      || asset.excludeFromRenewal || asset.cancellationDate) continue;
    if (!renewal) {
      add('MissingRenewalDate', 'unevaluable');
      continue;
    }
    const expected = format(subDays(parseISO(renewal), 1), 'yyyy-MM-dd');
    if (usageEnd && (!end || end >= expected) && usageEnd !== expected)
      add('RenewalMisalignment', 'warning', usageEnd, expected);
  }
  const assessedContracts = new Set<string>();
  for (const contract of contracts) {
    const add = (rule: SalesforceInternalRule, severity: SalesforceInternalSeverity,
      actual: string | null = null, expected: string | null = null, relatedAssets: SalesforceAssetDetail[] = []) =>
      addFinding(rule, severity, null, contract, actual, expected, relatedAssets);
    if (!contract.id) {
      add('MissingContractIdentity', 'unevaluable');
      continue;
    }
    if (assessedContracts.has(contract.id)) continue;
    assessedContracts.add(contract.id);
    if ((byId.get(contract.id)?.length ?? 0) !== 1) {
      add('AmbiguousContract', 'unevaluable', contract.id);
      continue;
    }
    const contractStatus = status(contract.status);
    const start = salesforceCalendarDate(contract.startDate);
    const end = salesforceCalendarDate(contract.terminationDate);
    const renewal = salesforceCalendarDate(contract.nextRenewalDate);
    if ((contract.startDate != null && !start) || (contract.terminationDate != null && !end)
      || (contract.nextRenewalDate != null && !renewal)) {
      add('InvalidDate', 'unevaluable');
      continue;
    }
    if (start && end && end < start) add('ContractEndBeforeStart', 'warning', end, start);
    if (start && renewal && renewal < start) add('ContractRenewalBeforeStart', 'warning', renewal, start);
    if (contractStatus === 'activated' && end && end < today)
      add('ActivatedWithPastEndDate', 'warning', end, today);
    if (contractStatus === 'expired') {
      if (!end) add('ExpiredWithoutEndDate', 'warning');
      else if (end >= today) add('ExpiredWithCurrentEndDate', 'warning', end, today);
    }
    const current = contractStatus === 'activated' && (!end || end >= today);
    if (current && renewal && renewal < today) add('SalesforceRenewalDatePassed', 'warning', renewal, today);
    const linked = assetsByContract.get(contract.id) ?? [];
    const active = linked.filter((asset) => status(asset.status) === 'active' && isRecurring(asset));
    if (current && active.length === 0) {
      const potentiallyUnlinked = unlinkedAssets.filter((asset) => {
        if (asset.accountId && contract.accountId) return asset.accountId === contract.accountId;
        return !asset.companyNumber || !contract.companyNumber || asset.companyNumber === contract.companyNumber;
      });
      const unknown = linked.concat(potentiallyUnlinked).filter((asset) =>
        !isNonRecurring(asset) && status(asset.status) !== 'inactive' && status(asset.status) !== 'cancelled');
      if (unknown.length > 0) add('IncompleteContractAssets', 'unevaluable', null, null, unknown);
      else add('ActiveContractWithoutAssets', 'error', '0', '>= 1', linked);
    }
    const dated = active.filter((asset) => salesforceCalendarDate(asset.usageEndDate) != null);
    const dates = [...new Set(dated.map((asset) => salesforceCalendarDate(asset.usageEndDate)))].sort();
    if (dates.length > 1) add('InconsistentAssetEndDates', 'error', dates.join(', '), null, dated);
  }
  const rank: Record<SalesforceInternalSeverity, number> = { error: 0, warning: 1, unevaluable: 2 };
  return findings.sort((left, right) => rank[left.severity] - rank[right.severity]
    || (left.asset?.companyNumber ?? left.contract?.companyNumber ?? '')
      .localeCompare(right.asset?.companyNumber ?? right.contract?.companyNumber ?? '')
    || (left.asset?.contractNumber ?? left.contract?.contractNumber ?? '')
      .localeCompare(right.asset?.contractNumber ?? right.contract?.contractNumber ?? '')
    || (left.asset?.id ?? '').localeCompare(right.asset?.id ?? '') || left.rule.localeCompare(right.rule));
}
